// frontend/src/components/reputation/CitationsWorklist.jsx
// Version: 1.0 (2026-09-16-postanovka-vkladka-citations.md)
// Description: Рабочий список регистраций в каталогах Tier-1 и сверки с каноном.
//
// 🔴 ПРАВИЛО НОЛЬ (постановление 2026-09-16):
//    Ни одного адреса, телефона, номера Bar или ссылки на каталог в JSX/константах.
//    Все данные приходят ИСКЛЮЧИТЕЛЬНО из ручки /api/v1/reputation/citations/summary.
//    При отсутствии замеров: «—» (прочерк), «не проводился», «не измеряется».

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CitationsFixPacket from './CitationsFixPacket';
import { useUiLanguage, perevesti } from '../../context/UiLanguageContext';
import {
  ShieldAlert,
  ExternalLink,
  Award,
  RefreshCw,
  Info,
  AlertTriangle,
  Building2,
} from 'lucide-react';

const ROUTE_LABELS = {
  both: 'Оба канона (главный + филиал)',
  main_only: 'Только главный',
  human_decides: 'Решает человек (уже числится по филиалу)',
};

const STATUS_LABELS = {
  MATCH: { text: 'Совпадает', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  MINOR_DRIFT: { text: 'Мелкий дрифт', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  CRITICAL_DRIFT: { text: 'Расхождение', color: 'text-rose-700 bg-rose-50 border-rose-200' },
  MISSING_LISTING: { text: 'Профиля нет', color: 'text-slate-600 bg-slate-100 border-slate-200' },
  NOT_MEASURED: { text: 'Не проверялось', color: 'text-slate-500 bg-slate-50 border-slate-200' },
  // 🔴 Заявка подана и рассматривается площадкой. Это НЕ «профиля нет»:
  // повторная подача при рассматриваемой заявке вредна, поэтому и вид, и
  // действие у этой строки отдельные.
  IN_REVIEW: { text: 'На рассмотрении', color: 'text-sky-800 bg-sky-50 border-sky-200' },
  // 🔴 Наблюдение есть, но ни одно ключевое поле NAP не сравнивалось
  NOT_COMPARABLE: { text: 'Сравнить не с чем', color: 'text-amber-800 bg-amber-50 border-amber-300' },
};

// Замечания приходят с сервера ключами; здесь только перевод для человека.
// Незнакомый ключ показывается как есть — молча проглоченное замечание хуже
// непереведённого.
const ISSUE_LABELS = {
  name_mismatch: 'имя фирмы не совпадает',
  name_is_attorney_not_firm: 'вместо фирмы указан адвокат',
  name_former_used: 'указана прежняя фамилия',
  phone_mismatch: 'телефон не совпадает',
  street_mismatch: 'улица записана иначе',
  suite_mismatch: 'номер офиса не совпадает',
  city_state_zip_mismatch: 'город / штат / индекс не совпадают',
  website_mismatch: 'сайт не совпадает',
  // 🔴 Канон велит НЕ «главный», а ОТДАТЬ ЧЕЛОВЕКУ: `locations_for` возвращает
  // здесь ROUTE_HUMAN. Прежний текст («канон велит главный») сообщал, что
  // решение уже принято, — и приглашал выполнить его механически в чужой
  // системе, где откат идёт через поддержку, а не правкой.
  listed_on_branch_but_route_is_main_only:
    'профиль заведён на филиал, а площадка одноместная — решение принимает человек',
  submitted_awaiting_platform: 'заявка подана, ждём площадку',
};

// 🔴 Экспортируется нарочно: пакет правок показывает те же коды замечаний.
// Второй словарь переводов разошёлся бы с этим молча и не сразу.
export const opisatZamechanie = (kod, t) => {
  if (typeof t === 'function') {
    const key = `rep.citations.issue.${kod}`;
    const tr = t(key);
    if (tr && !tr.startsWith('[')) return tr;
  }
  return ISSUE_LABELS[kod] || kod;
};

export const CitationsWorklist = ({ tenant = 'fitenko' }) => {
  const { lang: rawLang, t: contextT } = useUiLanguage();
  const lang = rawLang || 'ru';
  const isEn = lang === 'en';
  const t = useCallback((k, v) => (rawLang ? contextT(k, v) : perevesti('ru', k, v)), [rawLang, contextT]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pokazatPaket, setPokazatPaket] = useState(false);

  const getRouteLabel = (route) => {
    const key = `rep.citations.route.${route}`;
    const tr = t(key);
    return tr && !tr.startsWith('[') ? tr : (ROUTE_LABELS[route] || route);
  };

  const getStatusMeta = (status) => {
    const meta = STATUS_LABELS[status] || STATUS_LABELS.NOT_MEASURED;
    const key = `rep.citations.status.${status}`;
    const tr = t(key);
    return {
      text: tr && !tr.startsWith('[') ? tr : meta.text,
      color: meta.color,
    };
  };

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/v1/reputation/citations/summary');
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось получить сводку каталогов');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E1D6] rounded-2xl p-8 text-center space-y-3">
        <RefreshCw className="w-6 h-6 animate-spin text-[#C8A653] mx-auto" />
        <div className="text-sm text-[#687077]">{t('rep.citations.loading')}</div>
      </div>
    );
  }

  if (data?.status === 'not_configured' || data?.code === 'canon_not_configured') {
    return (
      <div data-testid="canon-not-configured" className="bg-white border border-[#E5E1D6] rounded-2xl p-8 text-center space-y-3">
        <Info className="w-6 h-6 text-[#C8A653] mx-auto" />
        <div className="text-sm font-medium text-[#20231F]">
          {t('rep.citations.canon_not_configured')}
        </div>
        <div className="text-xs text-[#687077]">
          {t('rep.citations.canon_not_configured_desc')}
        </div>
      </div>
    );
  }

  // 🔴 ТРИ СОСТОЯНИЯ МИРА, А НЕ ДВА: «данные получены», «данных нет»,
  // «прочитать не удалось». До 18.09 третьего на экране не было: ручка при
  // недоступном каноне отвечает УСПЕШНЫМ кодом и кладёт признак отказа в тело
  // (`error`), а компонент читал только исключение axios. Исключения нет, тело
  // получено, список пуст — и молчание прибора приходило к человеку в виде
  // хорошей новости «нарушений не найдено».
  const priznakOtkaza = error || data?.error || (!data ? 'ответ не получен' : null);

  if (priznakOtkaza) {
    return (
      <div className="bg-white border border-rose-200 rounded-2xl p-6 text-rose-800 space-y-3">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <AlertTriangle className="w-4 h-4 text-rose-600" />
          <span>{t('rep.citations.error.title')}</span>
        </div>
        <p className="text-xs text-rose-600">
          {t('rep.citations.error.desc', { flag: String(data?.error || priznakOtkaza) })}
          {data?.detail ? `; ${data.detail}` : ''}
        </p>
        <button
          onClick={fetchSummary}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-rose-100 hover:bg-rose-200 text-rose-900 transition-colors cursor-pointer"
        >
          {t('rep.citations.error.retry')}
        </button>
      </div>
    );
  }

  const { canon, measured_at, measured_count, total_count, overall_score, directories = [] } = data;

  return (
    <div className="space-y-6">
      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* 1. ШАПКА: 3 ВЕЛИЧИНЫ СО ЗНАМЕНАТЕЛЕМ И МОМЕНТОМ (§4 ПОСТАНОВКИ)       */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Карточка 1: Измерено */}
        <div className="bg-white border border-[#E5E1D6] rounded-2xl p-5 shadow-xs flex flex-col justify-between">
          <div>
            <div className="text-xs text-[#687077] font-medium uppercase tracking-wider">{t('rep.citations.coverage.title')}</div>
            <div className="text-2xl font-serif text-[#20231F] mt-2 font-bold">
              {t('rep.citations.measured_denominator', { n: measured_count, m: total_count })}
            </div>
          </div>
          <div className="text-xs text-[#687077] mt-3 pt-3 border-t border-[#F1EEE4] flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5 text-[#C8A653] shrink-0" />
            <span>
              {measured_at
                ? t('rep.citations.last_scan', {
                    date: /^\d{4}-\d{2}-\d{2}$/.test(measured_at)
                      ? measured_at
                      : new Date(measured_at).toLocaleString(),
                  })
                : (t('rep.citations.scan_never_run') || 'не проводился')}
              {(data.measured_method_code || data.measured_method) && (
                <span className="ml-1 text-[#687077]">
                  {data.measured_method_code
                    ? `(${t(`rep.citations.method.${data.measured_method_code}`)})`
                    : data.measured_method === 'human_screenshot'
                    ? t('rep.citations.method_human_screenshot')
                    : (isEn ? `(${t('rep.citations.method.human_snapshot')})` : `(${data.measured_method})`)}
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Карточка 2: Индекс */}
        <div className="bg-white border border-[#E5E1D6] rounded-2xl p-5 shadow-xs flex flex-col justify-between">
          <div>
            <div className="text-xs text-[#687077] font-medium uppercase tracking-wider">{t('rep.citations.consistency_index')}</div>
            <div className="text-2xl font-serif text-[#20231F] mt-2 font-bold">
              {overall_score !== null && overall_score !== undefined
                ? t('rep.citations.index_score', { score: overall_score })
                : 'Индекс: —'}
            </div>
          </div>
          <div className="text-xs text-[#687077] mt-3 pt-3 border-t border-[#F1EEE4]">
            {measured_at === null
              ? (t('rep.citations.no_network_scan') || 'сетевого опроса не было — величина не измеряется')
              : t('rep.citations.weighted_explanation', { n: measured_count, m: total_count })}
          </div>
        </div>

        {/* Карточка 3: Каноны */}
        <div className="bg-white border border-[#E5E1D6] rounded-2xl p-5 shadow-xs flex flex-col justify-between">
          <div>
            <div className="text-xs text-[#687077] font-medium uppercase tracking-wider">{t('rep.citations.active_canons')}</div>
            <div className="text-xs text-[#20231F] mt-2 space-y-1">
              {canon?.locations?.map((loc, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-[#C8A653] shrink-0" />
                  <span className="font-medium">{loc.label}</span>
                  <span className="text-[#687077]">
                    ({loc.role === 'MAIN' ? t('rep.citations.role_main') : t('rep.citations.role_branch') + (loc.street_visible ? '' : t('rep.citations.street_hidden'))})
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-[#8C7A5B] mt-3 pt-3 border-t border-[#F1EEE4] truncate" title={canon?.source_path}>
            {t('rep.citations.source', { source: canon?.source_path || t('rep.citations.canon_not_set') })}
          </div>
        </div>
      </div>

      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* 2. КРАСНАЯ ПОЛОСА «НИКОГДА НЕ ПУБЛИКОВАТЬ» (§4 ПОСТАНОВКИ)             */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      {canon?.never_publish && canon.never_publish.length > 0 && (
        <div className="border border-rose-200 bg-rose-50/80 rounded-2xl p-4 sm:p-5 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-rose-100 rounded-xl text-rose-700 shrink-0 mt-0.5">
                <ShieldAlert className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-rose-900">
                  {t('rep.citations.never_publish.title') || 'Никогда не публиковать в общедоступных каталогах'}
                </h4>
                <p className="text-xs text-rose-700 mt-0.5">
                  {t('rep.citations.never_publish.desc')}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:self-center">
              {canon.never_publish.map((item, idx) => (
                <span
                  key={idx}
                  className="px-2.5 py-1 rounded-lg text-xs font-mono font-bold bg-white text-rose-800 border border-rose-300 shadow-2xs"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* 3. ТАБЛИЦА КАТАЛОГОВ TIER-1 (РАБОЧИЙ СПИСОК РЕГИСТРАЦИЙ)              */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      <div className="bg-white border border-[#E5E1D6] rounded-2xl p-6 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-serif text-[#20231F]">{t('rep.citations.table.title')}</h3>
            <p className="text-xs text-[#687077] mt-1">
              {t('rep.citations.table.desc')}
            </p>
          </div>
          {/* 🔴 Переключатель, и только он: пакет собирается из УЖЕ полученного
              ответа. Ни нового запроса к ручке, ни тем более обращения к чужим
              площадкам — ручек записи к ним нет ни одной. */}
          <button
            type="button"
            onClick={() => setPokazatPaket((v) => !v)}
            className="shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] transition-colors cursor-pointer"
          >
            {pokazatPaket ? t('rep.citations.btn.collapse_packet') : t('rep.citations.btn.prepare_packet')}
          </button>
        </div>

        {/* Законная пустота говорит СВОИМИ словами. «Нечего править» и «не
            смогли прочитать» — разные состояния мира, и путать их нельзя. */}
        {directories.length === 0 && (
          <div className="text-xs text-[#687077] border border-[#E5E1D6] rounded-xl p-4">
            {t('rep.citations.empty_directories')}
          </div>
        )}

        {pokazatPaket && (
          <CitationsFixPacket canon={canon} directories={directories} />
        )}

        {data?.warnings && data.warnings.length > 0 && (
          <div className="border border-amber-200 bg-amber-50 rounded-xl p-3 text-xs text-amber-800 space-y-1">
            <div className="font-semibold flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
              <span>{t('rep.citations.warnings_parser')}</span>
            </div>
            <ul className="list-disc list-inside text-[11px] space-y-0.5">
              {data.warnings.map((w, idx) => (
                <li key={idx}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#E5E1D6] text-[11px] uppercase tracking-wider text-[#687077]">
                <th className="py-3 px-3 font-semibold">{t('rep.citations.th.directory')}</th>
                <th className="py-3 px-3 font-semibold">{t('rep.citations.th.route')}</th>
                <th className="py-3 px-3 font-semibold">{t('rep.citations.th.status')}</th>
                <th className="py-3 px-3 font-semibold text-center">{t('rep.citations.th.score_weight')}</th>
                <th className="py-3 px-3 font-semibold text-right">{t('rep.citations.th.action')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1EEE4] text-xs">
              {directories.map((dir) => {
                const isHumanDecides = dir.route === 'human_decides';
                const statusMeta = getStatusMeta(dir.status);
                const vseZamechaniya = dir.issues || [];
                const nenablyudalos = dir.unobserved || [];
                const zamechaniya = vseZamechaniya.filter((i) => !String(i).startsWith('not_observed:'));
                const zhdemPloshchadku = dir.next_action === 'await_platform';

                return (
                  <tr
                    key={dir.key || dir.slug || dir.name}
                    className={`transition-colors ${
                      isHumanDecides
                        ? 'bg-amber-50/60 hover:bg-amber-50'
                        : 'hover:bg-[#FCFBF8]'
                    }`}
                  >
                    {/* Колонка: Каталог */}
                    <td className="py-3.5 px-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[#20231F]">{dir.name}</span>
                        {dir.is_super_donor && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#F5EED8] text-[#8C6B1B] border border-[#E8DCB8]">
                            <Award className="w-3 h-3" />
                            {t('rep.citations.super_donor')}
                          </span>
                        )}
                      </div>
                      {(dir.confirmed_on || dir.method) && (
                        <div className="text-[10px] text-[#687077] mt-0.5">
                          {dir.confirmed_on && <span>{t('rep.citations.scan', { date: dir.confirmed_on })}</span>}
                          {dir.confirmed_on && dir.method && <span> &middot; </span>}
                          {dir.method && <span>{dir.method_code ? t(`rep.citations.method.${dir.method_code}`) : (isEn ? t('rep.citations.method.human_snapshot') : dir.method)}</span>}
                        </div>
                      )}
                      {dir.history && dir.history.length > 0 && (
                        <div className="mt-1 text-[10px] text-[#8C7A5B]">
                          <span className="font-medium">{t('rep.citations.history')}</span>
                          {dir.history.map((h, hIdx) => (
                            <span key={hIdx} className="inline-block bg-[#F8F7F2] border border-[#E5E1D6] rounded px-1.5 py-0.5 mr-1">
                              {h.confirmed_on}: {h.score !== undefined && h.score !== null ? `${h.score}%` : (h.platform_state || t('rep.plural.zamerov.one'))} ({h.method_code ? t(`rep.citations.method.${h.method_code}`) : (isEn ? t('rep.citations.method.human_snapshot') : (h.method || t('rep.citations.method.human_snapshot')))})
                            </span>
                          ))}
                        </div>
                      )}
                    </td>

                    {/* Колонка: Что подаём */}
                    <td className="py-3.5 px-3">
                      <div className="space-y-0.5">
                        <div
                          className={`font-medium text-[11px] ${
                            isHumanDecides ? 'text-amber-900 font-bold' : 'text-[#20231F]'
                          }`}
                        >
                          {getRouteLabel(dir.route)}
                        </div>
                        {isHumanDecides && (
                          <div className="text-[10px] text-amber-700 leading-tight">
                            {t('rep.citations.human_decides_desc')}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Колонка: Состояние */}
                    <td className="py-3.5 px-3">
                      <div className="space-y-1.5">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${statusMeta.color}`}
                        >
                          {statusMeta.text}
                        </span>

                        {dir.status === 'NOT_COMPARABLE' && (
                          <div className="text-[11px] text-amber-900 font-medium leading-tight">
                            {t('rep.citations.not_comparable_desc')}
                          </div>
                        )}
                        {dir.status === 'NOT_COMPARABLE' && dir.observed_address && (
                          <div className="text-[10px] text-[#687077] italic leading-tight">
                            {t('rep.citations.observed_prefix', { address: dir.observed_address })}
                          </div>
                        )}

                        {/* 🔴 Находки замера показываются поимённо. */}
                        {zamechaniya.length > 0 && (
                          <ul className="space-y-0.5">
                            {zamechaniya.map((kod) => (
                              <li
                                key={kod}
                                className={`text-[10px] leading-tight ${
                                  kod === 'адрес записан текстом, не сравнивается'
                                    ? 'text-[#8C7A5B]'
                                    : 'text-rose-800'
                                }`}
                              >
                                • {opisatZamechanie(kod, t)}
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Пустая ячейка наблюдения: на снимке не видно */}
                        {nenablyudalos.length > 0 && (
                          <div className="text-[10px] text-[#687077] leading-tight">
                            {t('rep.citations.unobserved', { items: nenablyudalos.join(', ') })}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Колонка: Скор · вес */}
                    <td className="py-3.5 px-3 text-center font-mono">
                      {dir.score !== null && dir.score !== undefined ? (
                        <>
                          <div className="font-bold text-[#20231F]">{dir.score}%</div>
                          <div className="text-[10px] text-[#687077] font-sans">{t('rep.citations.weight', { weight: dir.weight })}</div>
                        </>
                      ) : (
                        <>
                          <div className="text-[#687077]">—</div>
                          <div className="text-[10px] text-[#687077] font-sans">{t('rep.citations.not_in_calc')}</div>
                        </>
                      )}
                    </td>

                    {/* Колонка: Действие */}
                    <td className="py-3.5 px-3 text-right">
                      {zhdemPloshchadku ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium bg-sky-50 border border-sky-200 text-sky-800">
                          {t('rep.citations.action.await_platform')}
                          {/* Ждать площадку */}
                        </span>
                      ) : dir.profile_url ? (
                        <a
                          href={dir.profile_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] transition-colors cursor-pointer shadow-2xs"
                        >
                          <span>{t('rep.citations.action.open_profile')}</span>
                          <ExternalLink className="w-3 h-3 text-[#C8A653]" />
                        </a>
                      ) : dir.raw_url ? (
                        <div className="inline-flex flex-col items-end">
                          <span className="text-[11px] text-amber-800 font-medium">
                            {t('rep.citations.no_full_url')}
                          </span>
                          <span className="text-[10px] text-[#687077] font-mono max-w-[180px] truncate" title={dir.raw_url}>
                            {t('rep.citations.slug', { slug: dir.raw_url })}
                          </span>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium bg-[#F8F7F2] border border-[#E5E1D6] text-[#687077]">
                          {t('rep.citations.action.create_profile')}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CitationsWorklist;
